import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, Home, ListChecks, Bell, ShieldCheck, TrendingUp } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import AdminLayout from './AdminLayout';
import { getMetrics } from '../../lib/admin';

export default function AdminDashboard() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [metrics, setMetrics] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (user === false) { navigate('/login?next=/admin'); return; }
        if (user && user.role !== 'admin') { navigate('/'); return; }
        if (user) {
            getMetrics().then(setMetrics).finally(() => setLoading(false));
        }
    }, [user, navigate]);

    if (user === null) {
        return <div className="min-h-screen flex items-center justify-center bg-gray-50"><div className="w-10 h-10 border-2 border-brand/30 border-t-brand rounded-full animate-spin" /></div>;
    }

    return (
        <AdminLayout title="Dashboard">
            {loading ? (
                <div className="py-20 flex justify-center"><div className="w-8 h-8 border-2 border-brand/30 border-t-brand rounded-full animate-spin" /></div>
            ) : metrics && (
                <div className="space-y-6">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <Stat icon={Users} label="Usuarios" value={metrics.users.total} sub={`${metrics.users.verified} verificados`} color="bg-blue-50 text-blue-600" testId="stat-users" />
                        <Stat icon={Home} label="Familias" value={metrics.families.total} sub={`${metrics.invitations.pending} invitaciones pendientes`} color="bg-pink-50 text-pink-600" testId="stat-families" />
                        <Stat icon={ListChecks} label="Listas creadas" value={metrics.lists.total} sub={`${metrics.lists.elementos} elementos · ${metrics.lists.nota} notas`} color="bg-emerald-50 text-emerald-600" testId="stat-lists" />
                        <Stat icon={Bell} label="Broadcasts" value={metrics.broadcasts.sent} sub="Enviados al total" color="bg-violet-50 text-violet-600" testId="stat-broadcasts" />
                    </div>

                    <div className="grid md:grid-cols-2 gap-4">
                        <Card title="Crecimiento" icon={TrendingUp}>
                            <p className="text-3xl font-bold text-brand-navy" data-testid="stat-new-users-7d">+{metrics.users.new_7d}</p>
                            <p className="text-xs text-gray-500">usuarios nuevos en los últimos 7 días</p>
                        </Card>
                        <Card title="Seguridad" icon={ShieldCheck}>
                            <p className="text-3xl font-bold text-brand-navy" data-testid="stat-suspended">{metrics.users.suspended}</p>
                            <p className="text-xs text-gray-500">usuarios suspendidos</p>
                        </Card>
                    </div>

                    <div className="bg-white rounded-2xl border border-gray-200 p-5">
                        <h3 className="text-sm font-bold text-brand-navy mb-3">Accesos rápidos</h3>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                            <QuickLink to="/admin/branding" label="Cambiar logo y colores" />
                            <QuickLink to="/admin/broadcasts" label="Enviar mensaje masivo" />
                            <QuickLink to="/admin/users" label="Ver usuarios" />
                            <QuickLink to="/admin/plans" label="Editar precios" />
                        </div>
                    </div>
                </div>
            )}
        </AdminLayout>
    );
}

function Stat({ icon: Icon, label, value, sub, color, testId }) {
    return (
        <div className="bg-white rounded-2xl border border-gray-200 p-4" data-testid={testId}>
            <div className={`w-9 h-9 rounded-xl ${color} flex items-center justify-center mb-3`}>
                <Icon className="w-4 h-4" />
            </div>
            <p className="text-2xl font-bold text-brand-navy">{value}</p>
            <p className="text-[11px] uppercase tracking-wider font-bold text-gray-400 mt-0.5">{label}</p>
            {sub && <p className="text-[11px] text-gray-500 mt-1">{sub}</p>}
        </div>
    );
}
function Card({ title, icon: Icon, children }) {
    return (
        <div className="bg-white rounded-2xl border border-gray-200 p-5">
            <div className="flex items-center gap-2 mb-2 text-gray-500">
                <Icon className="w-4 h-4" />
                <span className="text-xs font-bold uppercase tracking-wider">{title}</span>
            </div>
            {children}
        </div>
    );
}
function QuickLink({ to, label }) {
    const navigate = useNavigate();
    return (
        <button onClick={() => navigate(to)} className="text-left bg-gray-50 hover:bg-brand/5 rounded-xl p-3 text-sm text-brand-navy font-semibold transition">
            {label} →
        </button>
    );
}
